import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { MapIcon, ReceiptText } from 'lucide-react';
import { CircularLoading } from '@zawarski/eti-ui';
import {
  BottomNavigation,
  DualTitleHeader,
  HeaderComponent,
  HeaderMenuButton,
  LEGACY_MOBILE_SIZE,
  MoreMenu,
  MuiTableGrid,
} from '@components/rep';
import type { MuiTableColumn, SortOrder } from '@components/rep';
import StatusPill from '@components/StatusPill';
import {
  APP_TITLE,
  HOST_COUNTIES,
  LCERT_STATUS,
  PROGRAM_TITLE,
  RISK_COUNTIES,
} from '@constants/index';
import { useAccess } from '@hooks/useAccess';
import { useIsMobile } from '@hooks/useIsMobile';
import { useRepCountiesQuery } from '@services/groupsApi';
import { useLcertYearsQuery, useListLcertQuery } from '@services/lcertApi';
import type { Lcert } from '@/types';
import ChangeYearDialog from './ChangeYearDialog';

type HomeTab = 'welcome' | 'letters';

const REP_COUNTY_SET = new Set([
  ...Object.values(RISK_COUNTIES).flat(),
  ...Object.values(HOST_COUNTIES).flat(),
]);

/**
 * Home, as in the legacy dashboard: the "REP" header bar, the plants map
 * image, and for state users the bottom navigation to the Annual Letters of
 * Certification overview (county / status table for one year).
 */
const HomePage = ({ tab }: { tab: HomeTab }) => {
  const navigate = useNavigate();
  const { isStateUser, groupName } = useAccess();

  useEffect(() => {
    document.title = tab === 'letters' ? `Letters overview - ${APP_TITLE}` : APP_TITLE;
  }, [tab]);

  // County users outside the plant EPZs have nothing to do here (legacy DBCounty rule).
  if (!isStateUser && groupName && !REP_COUNTY_SET.has(groupName)) {
    return (
      <div className='rep:flex rep:h-full rep:w-full rep:flex-col'>
        <HeaderComponent icon={<HeaderMenuButton />} title={PROGRAM_TITLE} />
        <div className='rep:flex rep:flex-1 rep:flex-col rep:items-center rep:justify-center rep:gap-2 rep:p-6 rep:text-center'>
          <p className='rep:text-2xl rep:font-normal'>
            {groupName} users are currently not allowed access to the Radiological Emergency
            Preparedness site.
          </p>
          <p className='rep:text-2xl rep:font-normal'>
            Please contact the Radiological Emergency Preparedness Manager at SCEMD to gain access
            to the site.
          </p>
        </div>
      </div>
    );
  }

  const nav = isStateUser ? (
    <BottomNavigation
      options={[
        { id: 'welcome', icon: <MapIcon className='rep:size-5' />, title: 'Welcome' },
        {
          id: 'letters',
          icon: <ReceiptText className='rep:size-5' />,
          title: 'Annual Letters of Certification Overview',
        },
      ]}
      selectedItem={tab}
      onChange={(next) => navigate(next === 'welcome' ? '/' : '/letters')}
    />
  ) : null;

  if (tab === 'letters' && isStateUser) {
    return (
      <div
        className='rep:relative rep:flex rep:h-full rep:w-full rep:flex-col'
        style={{ paddingBottom: 48 }}>
        <LettersOverview />
        {nav}
      </div>
    );
  }

  return (
    <div className='rep:relative rep:flex rep:h-full rep:w-full rep:flex-col'>
      <HeaderComponent icon={<HeaderMenuButton />} title={PROGRAM_TITLE} />
      <div
        className='rep:min-h-0 rep:flex-1'
        style={{ padding: 24, paddingBottom: isStateUser ? 72 : 24 }}>
        <img
          src='/images/plants_map.png'
          alt='Nuclear plants and EPZ counties'
          style={{ objectFit: 'contain', width: '100%', height: '100%' }}
        />
      </div>
      {nav}
    </div>
  );
};

interface LetterRow extends Record<string, unknown> {
  pvGroupID: number;
  pvGroupName: string;
  pvStatus: string;
  letter?: Lcert;
}

const COLUMNS: MuiTableColumn<LetterRow>[] = [
  { field: 'pvGroupName', headerName: 'County', minWidth: 200 },
  {
    field: 'pvStatus',
    headerName: 'Status',
    minWidth: 200,
    renderCell: (row) => <StatusPill status={row.pvStatus} fontSize={14} />,
  },
];

/**
 * Legacy DBLetters: every REP county with the status of its latest letter for
 * the selected year (missing = Incomplete), title "REP - <year>", and a
 * "Change Year" menu opening the year dialog.
 */
const LettersOverview = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile(LEGACY_MOBILE_SIZE);
  const { data: years = [], isLoading: yearsLoading } = useLcertYearsQuery();
  const [year, setYear] = useState<string | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState('pvGroupID');

  const availableYears = useMemo(() => {
    const set = new Set(years.map(String));
    set.add(String(new Date().getFullYear()));
    return [...set].sort((a, b) => Number(a) - Number(b));
  }, [years]);
  const selected = year ?? availableYears[availableYears.length - 1];

  const { data: counties = [], isLoading: countiesLoading } = useRepCountiesQuery({
    year: selected,
  });
  const { data: letters = [], isLoading: lettersLoading } = useListLcertQuery(
    { year: selected, state: true },
    { skip: !selected },
  );

  const rows = useMemo<LetterRow[]>(() => {
    const byCounty = new Map<number, Lcert>();
    for (const l of letters) {
      const current = byCounty.get(l.pvCounty);
      if (!current || l.pvDataID > current.pvDataID) byCounty.set(l.pvCounty, l);
    }
    const list = counties.map((c) => {
      const letter = byCounty.get(c.pvGroupID);
      return {
        pvGroupID: c.pvGroupID,
        pvGroupName: c.pvGroupName,
        pvStatus: letter?.pvStatus || LCERT_STATUS.incomplete,
        letter,
      };
    });
    const dir = order === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [counties, letters, order, orderBy]);

  const loading = yearsLoading || countiesLoading || lettersLoading;

  return (
    <>
      <ChangeYearDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        years={availableYears}
        value={selected}
        onChange={setYear}
      />
      <DualTitleHeader
        icon={<HeaderMenuButton />}
        titleBig={`${PROGRAM_TITLE} - ${selected ?? ''}`}
        titleSmall={loading ? 'Loading' : `${rows.length} results`}
        suffix={<MoreMenu items={[{ title: 'Change Year', onClick: () => setDialogOpen(true) }]} />}
      />
      {loading ? (
        <div className='rep:flex rep:flex-1 rep:items-center rep:justify-center'>
          <CircularLoading size='md' />
        </div>
      ) : (
        <div className='rep:relative rep:flex rep:min-h-0 rep:flex-1 rep:flex-col'>
          <MuiTableGrid
            columns={COLUMNS}
            rows={rows}
            rowKey={(row) => row.pvGroupID}
            isMobile={isMobile}
            order={order}
            orderBy={orderBy}
            onRequestSort={(field) => {
              setOrder(orderBy === field && order === 'asc' ? 'desc' : 'asc');
              setOrderBy(field);
            }}
            onRowClick={(row) => row.letter && navigate(`/lcert/${row.letter.pvDataID}`)}
            renderMobileCard={(row) => (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 8px',
                  background: '#fff',
                }}>
                <span style={{ fontWeight: 600 }}>{row.pvGroupName}</span>
                <StatusPill status={row.pvStatus} fontSize={12} />
              </div>
            )}
          />
        </div>
      )}
    </>
  );
};

export default HomePage;
